/**
 * Sistema de Rede
 * Gerencia conectividade, status online/offline, informações de rede
 */

import { 
  INetworkService, 
  NetworkInfo, 
  ConnectionType, 
  EffectiveType,
  NetworkEvent,
  NetworkEventListener,
  ConnectivityTestResult,
  QualityMonitorResult,
  OptimizationRecommendations
} from '../interfaces/network_service';

export class NetworkSystem implements INetworkService {
  readonly serviceName = 'Network' as const;
  isInitialized = false;
  
  private networkInfo: NetworkInfo | null = null;
  private listeners: Set<NetworkEventListener> = new Set();

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    console.log('🌐 Inicializando Sistema de Rede...');

    try {
      // Detectar informações iniciais da rede
      this.detectNetworkInfo();
      
      // Configurar listeners de eventos
      this.setupEventListeners();

      this.isInitialized = true;
      console.log('✅ Sistema de Rede inicializado');
    } catch (error) {
      console.error('❌ Erro ao inicializar Sistema de Rede:', error);
      throw error;
    }
  }

  private detectNetworkInfo(): void {
    const connection = (navigator as any).connection;
    
    this.networkInfo = {
      isOnline: navigator.onLine,
      type: this.getConnectionType(connection),
      effectiveType: this.getEffectiveType(connection),
      downlink: connection?.downlink || 0,
      rtt: connection?.rtt || 0,
      saveData: connection?.saveData || false,
      timestamp: Date.now()
    };

    console.log('📊 Informações de rede detectadas:', this.networkInfo);
  }

  private getConnectionType(connection: any): ConnectionType {
    if (!connection?.type) {
      // Fallback: tentar detectar por outras características
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      return isMobile ? ConnectionType.CELLULAR : ConnectionType.WIFI;
    }

    switch (connection.type) {
      case 'wifi': return ConnectionType.WIFI;
      case 'cellular': return ConnectionType.CELLULAR;
      case 'ethernet': return ConnectionType.ETHERNET;
      case 'bluetooth': return ConnectionType.BLUETOOTH;
      case 'wimax': return ConnectionType.WIMAX;
      case 'none': return ConnectionType.NONE;
      case 'other': return ConnectionType.OTHER;
      default: return ConnectionType.UNKNOWN;
    }
  }

  private getEffectiveType(connection: any): EffectiveType {
    if (!connection?.effectiveType) {
      return EffectiveType.FOUR_G; // Default optimista
    }

    switch (connection.effectiveType) {
      case 'slow-2g': return EffectiveType.SLOW_2G;
      case '2g': return EffectiveType.TWO_G;
      case '3g': return EffectiveType.THREE_G;
      case '4g': return EffectiveType.FOUR_G;
      default: return EffectiveType.FOUR_G;
    }
  }

  private setupEventListeners(): void {
    // Listener para mudanças online/offline
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Listener para mudanças de conexão (se disponível)
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connection.addEventListener('change', this.handleConnectionChange.bind(this));
    }

    console.log('🎧 Event listeners de rede configurados');
  }

  private handleOnline(): void {
    console.log('🟢 Conexão estabelecida');
    this.detectNetworkInfo();
    
    if (this.networkInfo) {
      const event: NetworkEvent = {
        type: 'online',
        info: this.networkInfo,
        timestamp: Date.now()
      };

      this.notifyListeners(event);
    }
  }

  private handleOffline(): void {
    console.log('🔴 Conexão perdida');
    this.detectNetworkInfo();
    
    if (this.networkInfo) {
      const event: NetworkEvent = {
        type: 'offline',
        info: this.networkInfo,
        timestamp: Date.now()
      };

      this.notifyListeners(event);
    }
  }

  private handleConnectionChange(): void {
    console.log('🔄 Mudança na conexão detectada');
    this.detectNetworkInfo();
    
    if (this.networkInfo) {
      const event: NetworkEvent = {
        type: 'change',
        info: this.networkInfo,
        timestamp: Date.now()
      };

      this.notifyListeners(event);
    }
  }

  private notifyListeners(event: NetworkEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Erro em listener de rede:', error);
      }
    });
  }

  getNetworkInfo(): NetworkInfo {
    if (!this.networkInfo) {
      this.detectNetworkInfo();
    }
    
    return this.networkInfo || {
      isOnline: navigator.onLine,
      type: ConnectionType.UNKNOWN,
      effectiveType: EffectiveType.FOUR_G,
      downlink: 0,
      rtt: 0,
      saveData: false,
      timestamp: Date.now()
    };
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  isOffline(): boolean {
    return !navigator.onLine;
  }

  addListener(listener: NetworkEventListener): () => void {
    this.listeners.add(listener);
    
    // Retornar função para remover o listener
    return () => {
      this.listeners.delete(listener);
    };
  }

  removeListener(listener: NetworkEventListener): void {
    this.listeners.delete(listener);
  }

  removeAllListeners(): void {
    this.listeners.clear();
  }

  async testConnectivity(url?: string, timeout: number = 5000): Promise<ConnectivityTestResult> {
    const testUrl = url || '/favicon.ico';
    const startTime = performance.now();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(testUrl, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const responseTime = performance.now() - startTime;

      return {
        isReachable: response.ok,
        responseTime,
      };
    } catch (error) {
      const responseTime = performance.now() - startTime;
      
      return {
        isReachable: false,
        responseTime,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      };
    }
  }

  async monitorQuality(duration: number = 30000, interval: number = 1000): Promise<QualityMonitorResult> {
    const samples: number[] = [];
    let successCount = 0;
    const totalSamples = Math.floor(duration / interval);

    for (let i = 0; i < totalSamples; i++) {
      try {
        const result = await this.testConnectivity();
        samples.push(result.responseTime);
        
        if (result.isReachable) {
          successCount++;
        }
      } catch (error) {
        console.warn(`Amostra ${i + 1} falhou:`, error);
      }

      // Aguardar antes da próxima amostra (exceto na última)
      if (i < totalSamples - 1) {
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    const averageRtt = samples.length > 0 
      ? samples.reduce((sum, sample) => sum + sample, 0) / samples.length 
      : 0;

    const successRate = totalSamples > 0 ? successCount / totalSamples : 0;

    return {
      averageRtt,
      successRate,
      samples: samples.length
    };
  }

  isFastConnection(): boolean {
    const info = this.getNetworkInfo();
    
    // Baseado no effective type
    if (info.effectiveType === EffectiveType.FOUR_G) {
      return true;
    }

    // Baseado no RTT
    if (info.rtt > 0 && info.rtt < 200) {
      return true;
    }

    // Baseado no downlink
    if (info.downlink > 2) {
      return true;
    }

    return false;
  }

  isSlowConnection(): boolean {
    const info = this.getNetworkInfo();
    
    // Baseado no effective type
    if (info.effectiveType === EffectiveType.SLOW_2G || info.effectiveType === EffectiveType.TWO_G) {
      return true;
    }

    // Baseado no RTT
    if (info.rtt > 0 && info.rtt > 500) {
      return true;
    }

    // Baseado no downlink
    if (info.downlink > 0 && info.downlink < 0.5) {
      return true;
    }

    return false;
  }

  shouldSaveData(): boolean {
    const info = this.getNetworkInfo();
    return info.saveData || this.isSlowConnection();
  }

  getOptimizationRecommendations(): OptimizationRecommendations {
    const isFast = this.isFastConnection();
    const isSlow = this.isSlowConnection();
    const shouldSave = this.shouldSaveData();

    return {
      preloadImages: isFast && !shouldSave,
      useWebP: true, // Sempre recomendado
      enableAutoplay: isFast && !shouldSave,
      maxVideoQuality: isFast ? '1080p' : isSlow ? '360p' : '720p',
      batchRequests: isSlow || shouldSave
    };
  }

  getStatus(): any {
    return {
      serviceName: this.serviceName,
      isInitialized: this.isInitialized,
      networkInfo: this.networkInfo,
      listeners: this.listeners.size,
      connectionQuality: {
        isFast: this.isFastConnection(),
        isSlow: this.isSlowConnection(),
        shouldSaveData: this.shouldSaveData()
      },
      capabilities: {
        hasNetworkInfoAPI: 'connection' in navigator,
        hasOnlineAPI: 'onLine' in navigator,
        hasFetchAPI: typeof fetch !== 'undefined'
      }
    };
  }

  cleanup(): void {
    // Remover event listeners
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));

    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connection.removeEventListener('change', this.handleConnectionChange.bind(this));
    }

    // Limpar listeners
    this.listeners.clear();

    this.networkInfo = null;
    this.isInitialized = false;
    console.log('🧹 NetworkSystem limpo');
  }
}
